import { ThunkExperiments } from '@library/experimentation'

import { actionTypes } from 'actions/actionTypes'
import { promoApply, promoChange } from 'actions/promos'
import { redirect } from 'actions/redirect'
import { clickGetStarted } from 'actions/trackingKeys'
import {
  BLACK_FRIDAY_DISCOUNT,
  DISCOUNT_SOFTENING_PROMO_CODE_DATA_V5,
  PROMO_CODE_DATA,
} from 'routes/Home/homeConfig'
import { getPromoBannerState, hasConsentForAnalytics } from 'routes/Home/homeUtils'
import { ThunkActionCreator } from 'routes/Home/types/ThunkActionCreator'
import { getIsAuthenticated } from 'selectors/auth'
import { getUTMAndPromoCode } from 'selectors/tracking'
import logger from 'utils/logger'

type ExperimentDecision = {
  enabled: boolean
  variationKey: string | undefined | null
}

export const applyPromoCodeAndShowModal: ThunkActionCreator =
  () =>
  async (dispatch, getState, { experiments }: ThunkExperiments) => {
    const state = getState()
    const isAuthenticated = getIsAuthenticated(state)
    const hasAcceptedCookies = hasConsentForAnalytics()
    const shouldBeAllocatedToExperiment = !isAuthenticated && hasAcceptedCookies
    const { canApplyPromo } = getPromoBannerState(state)
    if (!canApplyPromo) {
      return
    }

    const getExperimentDecision = (experimentName: string): ExperimentDecision =>
      experiments && shouldBeAllocatedToExperiment
        ? experiments.decide(experimentName)
        : { enabled: false, variationKey: undefined }

    const { enabled: blackFridayEnabled, variationKey: blackFridayVariationKey } =
      getExperimentDecision('beetroots_ofx_black_friday_promo')

    const { enabled: discountSofteningEnabled, variationKey: discountSofteningVariationKey } =
      getExperimentDecision('beetroots_ofx_discount_softening_v5')

    const isBlackFridayPromotionEnabled = blackFridayEnabled && blackFridayVariationKey === 'on'
    const isDiscountSofteningV5Enabled =
      discountSofteningEnabled && discountSofteningVariationKey === 't1'

    let promoCode = PROMO_CODE_DATA.defaultPromoCode
    if (isBlackFridayPromotionEnabled) {
      promoCode = BLACK_FRIDAY_DISCOUNT.defaultPromoCode
    } else if (isDiscountSofteningV5Enabled) {
      promoCode = DISCOUNT_SOFTENING_PROMO_CODE_DATA_V5.defaultPromoCode
    }

    try {
      await dispatch(promoChange(promoCode))
      await dispatch(promoApply())
    } catch (err) {
      logger.warning(`error fetching promo code ${promoCode} - ${(err as Error).message}`)
    }
  }

export const trackGetStarted: ThunkActionCreator =
  (trackingSectionName: string) =>
  (dispatch, getState, { experiments }) => {
    const { promoCode, UTM } = getUTMAndPromoCode(getState())
    experiments.track('start_onboarding')
    dispatch({
      type: actionTypes.TRACKING,
      trackingData: {
        actionType: clickGetStarted,
        section: trackingSectionName,
        ...UTM,
        promoCode,
      },
    })
  }

export const homeGetStarted: ThunkActionCreator =
  (ctaUri: string, sectionForTracking?: string) => async (dispatch) => {
    if (sectionForTracking) {
      dispatch(trackGetStarted(sectionForTracking))
    }

    await dispatch(applyPromoCodeAndShowModal())

    dispatch(redirect(ctaUri))
  }
